/**
 * Generates a tiny base64 data URL blur placeholder from an image File.
 * Resizes to maxWidth on the longest side, re-encodes as JPEG at low quality,
 * and returns a data:image/jpeg;base64,... string suitable for inline img src.
 */
export async function generateBlurBase64(
  file: File,
  maxWidth = 40,
  quality = 0.2,
): Promise<string> {
  if (!file.type.startsWith('image/')) return ''

  return new Promise((resolve) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      const { naturalWidth: w, naturalHeight: h } = img
      const scale = maxWidth / Math.max(w, h)
      const targetW = Math.max(1, Math.round(w * scale))
      const targetH = Math.max(1, Math.round(h * scale))

      const canvas = document.createElement('canvas')
      canvas.width = targetW
      canvas.height = targetH

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve('')
        return
      }

      ctx.drawImage(img, 0, 0, targetW, targetH)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve('')
            return
          }
          const reader = new FileReader()
          reader.onloadend = () => {
            const result = reader.result as string
            resolve(result)
          }
          reader.readAsDataURL(blob)
        },
        'image/jpeg',
        quality,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve('')
    }

    img.src = objectUrl
  })
}
